import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Alert,
  useTheme,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  Info as InfoIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import DOMPurify from 'dompurify';
import Button from './Button';
import Card from './Card';

const Labyrinth = ({ content }) => {
  const theme = useTheme();
  const [currentNode, setCurrentNode] = useState(null);
  const [path, setPath] = useState([]);
  const [labyrinthData, setLabyrinthData] = useState(null);
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState(false);

  // Parse content from JSON
  useEffect(() => {
    try {
      // Проверяем, что content определен и не пустой
      if (content) {
        const parsedContent = JSON.parse(content);
        setLabyrinthData(parsedContent);
        // Start with the initial node
        if (parsedContent.startNode) {
          setCurrentNode(parsedContent.startNode);
          setPath([parsedContent.startNode.id]);
        } else {
          setError('Invalid labyrinth data: missing start node');
        }
      } else {
        console.warn('Labyrinth: content is undefined or empty');
        setError('Error loading content: no data provided');
      }
    } catch (error) {
      console.error('Error parsing labyrinth content:', error);
      setError('Error loading content. Please contact support.');
    }
  }, [content]);

  // Handle selecting a path option
  const handleSelectOption = (option) => {
    if (!labyrinthData || !labyrinthData.nodes) return;

    // Find the next node based on the option selected
    const nextNode = labyrinthData.nodes.find(
      (node) => node.id === option.nextNodeId
    );

    if (nextNode) {
      setCurrentNode(nextNode);
      setPath([...path, nextNode.id]);

      // Check if this is an end node
      if (nextNode.isEndNode) {
        setCompleted(true);
      }
    } else {
      setError(`Path error: Node with ID ${option.nextNodeId} not found`);
    }
  };

  // Handle restart
  const handleRestart = () => {
    if (labyrinthData && labyrinthData.startNode) {
      setCurrentNode(labyrinthData.startNode);
      setPath([labyrinthData.startNode.id]);
      setCompleted(false);
      setError(null);
    }
  };

  if (error) {
    return (
      <Box sx={{ my: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!labyrinthData || !currentNode) {
    return (
      <Box sx={{ my: 4 }}>
        <Typography>Loading content...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', my: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          textAlign: 'center',
          mb: 3,
          backgroundColor: theme.palette.primary.light,
          color: theme.palette.primary.contrastText,
        }}
      >
        <Typography variant="h6">
          {labyrinthData.title || 'Interactive Learning Labyrinth'}
        </Typography>
      </Paper>

      {/* Path visualization */}
      <Box sx={{ mb: 4 }}>
        <Stepper activeStep={path.length - 1} alternativeLabel>
          {path.map((nodeId, index) => {
            const nodeTitle =
              labyrinthData.nodes.find((n) => n.id === nodeId)?.title ||
              `Step ${index + 1}`;
            return (
              <Step key={index}>
                <StepLabel>{nodeTitle}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>

      {/* Current node content */}
      <Card sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          {currentNode.title}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Box
          sx={{ mt: 2 }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(currentNode.content),
          }}
        />

        {currentNode.info && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 3,
              backgroundColor: theme.palette.info.light,
              color: theme.palette.info.contrastText,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <InfoIcon sx={{ mr: 1 }} />
              <Typography variant="subtitle1">
                Additional Information
              </Typography>
            </Box>
            <Typography variant="body2">{currentNode.info}</Typography>
          </Paper>
        )}
      </Card>

      {/* Path options */}
      {!completed && currentNode.options && currentNode.options.length > 0 ? (
        <Box>
          <Typography variant="h6" gutterBottom>
            Choose your path:
          </Typography>
          <Grid container spacing={2}>
            {currentNode.options.map((option, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{
                    p: 2,
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    height: '100%',
                  }}
                  onClick={() => handleSelectOption(option)}
                  endIcon={<ArrowForwardIcon />}
                >
                  <Typography>{option.text}</Typography>
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : completed ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            <CheckIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            You've completed this learning path!
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRestart}
            sx={{ mt: 2 }}
          >
            Start Again
          </Button>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center' }}>
          <Typography color="error">
            No path options available. This appears to be a dead end.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleRestart}
            sx={{ mt: 2 }}
          >
            Start Over
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Labyrinth;
